import { ImageType, isAvailable } from "../../../types/baseTypes";

import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { Header_Pages } from "@/wp/types/acf-header-types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@/wp/types/client";
import { ParentsType } from "@/wp/getParentPages";
import { Content } from "@/components/Content/Content";
import { useRouter } from "next/router";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";
import { Headline } from "@/components/Headline/Headline";
import { UniversalHeader } from "../UniversalHeader/UniversalHeader";
import { Button } from "@/components/Button/Button";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { passACFLink } from "@/utils/passACFLink";
// import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

export interface SeiteHeaderProps {
  post: Post<"page" | "noble_elements">;
  headerOptions?: HeadersOptionsType["header_pages"];
  parents: ParentsType;
}

export const isAnyImageAvailable = (
  post: Header_Pages["header_seite"],
): boolean => {
  return (
    isAvailable<ImageType>(post.media.image_fullhd) ||
    isAvailable<ImageType>(post.media.image_desktop) ||
    isAvailable<ImageType>(post.media.image_tablet) ||
    isAvailable<ImageType>(post.media.image_mobile)
  );
};
/* jscpd:ignore-start */
export const SeiteHeader = ({
  post,
  headerOptions,
  parents,
}: SeiteHeaderProps) => {
  if ("header_startseite" in post.acf) {
    throw new Error(
      "SeiteHeader cannot be used for post type with header_staseitert",
    );
  }
  const header = post.acf.header_seite;
  const router = useRouter();

  /* const { getIcon } = useGlobalContext(); */

  const {
    overline = "",
    subline = "",
    media,
    type,
    background_color_class,
    section_margin_class,
    link,
    link_extended,
    content,
    headline_text_align_class,
    // row_direction_class,
    seo_settings: { breadcrumb_title } = {},
    image_small,
  } = header;

  const {
    show_overline_subline,
    textheader,
    splitheader,
    show_extended_link,
    button_type,
  } = headerOptions ?? {};

  const { text_width: textheaderTextWidth, text_horizontal_align } =
    textheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
  } = splitheader ?? {};

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const mediaJSX =
    media && media.type === "image" ? (
      <MediaItemResponsive
        image_desktop={media.image_desktop}
        image_tablet={media.image_tablet}
        image_mobile={media.image_mobile}
        image_fullhd={media.image_fullhd}
        sizes="100vw"
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
        figcaption={media.show_caption && media.caption ? media.caption : ""}
        caption_position={media.caption_position}
      />
    ) : (
      <></>
    );

  const contentJSX = (
    <>
      <Headline
        headline={header.title}
        headline_html_tag="h1-pages"
        extraClass={`is-flex is-flex-direction-column ${headline_text_align_class === "has-text-centered" ? "is-align-items-center" : ""}`}
        overline={showOverline ? overline : ""}
        subline={
          router.query.keyword
            ? (router.query.keyword as string)
            : showSubline
              ? subline
              : ""
        }
        disableAnimation
      />
      <Content
        headline=""
        headline_html_tag="h2"
        headline_text_align_class={headline_text_align_class}
        text_align_class={headline_text_align_class}
        button_type="primary"
        text={content}
        link={
          !show_extended_link &&
          type !== "splitheader" &&
          type !== "imageheader" &&
          link
        }
        link_extended={
          show_extended_link &&
          type !== "splitheader" &&
          type !== "imageheader" &&
          link_extended
        }
        isHeader={true}
      />
    </>
  );

  const linkJSX =
    type === "imageheader" ? (
      show_extended_link && link_extended ? (
        <ExtendedLink
          extendedLink={link_extended}
          btnType={button_type ?? "primary"}
        />
      ) : (
        link &&
        link.url && (
          <Button
            type={button_type ?? "primary"}
            {...passACFLink({ link }, "link")}
          >
            {link.title}
          </Button>
        )
      )
    ) : undefined;

  const breadcrumbsJSX = (
    <Breadcrumbs
      extraClass={"breadcrumb"}
      parents={parents}
      breadcrumbPositon={"is-justify-content-flex-start"}
      breadcrumbTitle={breadcrumb_title}
      showOnMobile
    />
  );

  switch (type) {
    case "textheader":
    case "splitheader":
    case "imageheader":
      return (
        <UniversalHeader
          type={type}
          mediaJSX={mediaJSX}
          contentJSX={contentJSX}
          breadcrumbsJSX={breadcrumbsJSX}
          background_color_class={background_color_class}
          section_margin_class={section_margin_class}
          textheaderTextWidth={textheaderTextWidth}
          text_horizontal_align={text_horizontal_align}
          image_small={image_small}
          link={link}
          linkJSX={linkJSX}
        />
      );
  }
};
/* jscpd:ignore-end */
